// LiveSimulatorPanel.js — "Start today" mode within InvestorSimulatorPage.
// Shows live simulation badge, countdown, timeline, daily marks, and controls.

import React, { useEffect, useState, useCallback } from 'react';
import { useSimulator } from '../../context/SimulatorContext';
import LiveTimeline from './LiveTimeline';
import ProjectionOverlay from './ProjectionOverlay';
import CashflowLedger from './CashflowLedger';
import { Clock, XCircle, History, RefreshCw } from 'lucide-react';

const STATUS_LABELS = {
  pending_auction: 'Waiting for Auction',
  settled: 'Settled — Holding',
  matured: 'Matured',
  rolled: 'Rolled Over',
  completed: 'Completed',
  cancelled: 'Cancelled',
  awaiting_data: 'Awaiting Market Data',
};

const STATUS_COLORS = {
  pending_auction: 'bg-amber-100 text-amber-800',
  settled: 'bg-emerald-100 text-emerald-800',
  matured: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  awaiting_data: 'bg-orange-100 text-orange-800',
};

function CountdownToEvent({ nextEventDate, nextEventType }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!nextEventDate) return;

    const update = () => {
      const now = new Date();
      const target = new Date(nextEventDate);
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft('Due');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else {
        setTimeLeft(`${hours}h`);
      }
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [nextEventDate]);

  if (!nextEventDate) return null;

  const eventLabel = nextEventType === 'auction' ? 'Next Auction'
    : nextEventType === 'maturity' ? 'Maturity'
    : nextEventType === 'exit' ? 'Exit'
    : 'Next Event';

  return (
    <div className="flex items-center gap-2 text-xs bg-blue-50 text-blue-700 rounded-lg px-3 py-2">
      <Clock className="w-3.5 h-3.5" />
      <span>{eventLabel}: <strong>{nextEventDate}</strong></span>
      <span className="ml-auto font-mono font-medium">{timeLeft}</span>
    </div>
  );
}

function LiveRunCard({ run, isActive, onClick }) {
  const statusColor = STATUS_COLORS[run.status] || 'bg-gray-100 text-gray-800';

  return (
    <button
      onClick={() => onClick(run.id)}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${
        isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-mono text-gray-500">
          {run.investment_amount ? `$${Number(run.investment_amount).toLocaleString()}` : '-'}
        </span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusColor}`}>
          {STATUS_LABELS[run.status] || run.status}
        </span>
      </div>
      <div className="text-[10px] text-gray-400">
        {run.tenor_days}-day {run.instrument} | {run.forward_mode !== 'none' ? 'Hedged' : 'Unhedged'}
      </div>
      <div className="text-[10px] text-gray-400 mt-0.5">
        Started: {run.start_date || run.created_at?.split('T')[0]}
      </div>
    </button>
  );
}

export default function LiveSimulatorPanel() {
  const {
    activeLiveRun, liveRunDetail, liveRuns, loading, error,
    loadLiveRuns, loadLiveRunDetail, cancelLiveRun, createLiveRun, params,
  } = useSimulator();

  const [activeTab, setActiveTab] = useState('overview');
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    loadLiveRuns();
  }, [loadLiveRuns]);

  const handleCreateRun = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    await createLiveRun({
      ...params,
      startDate: today,
      endDate: params.endDate || endDate.toISOString().split('T')[0],
    });
  }, [createLiveRun, params]);

  const handleCancel = useCallback(async () => {
    if (!activeLiveRun) return;
    if (window.confirm('Cancel this live simulation run?')) {
      await cancelLiveRun(activeLiveRun.id);
    }
  }, [activeLiveRun, cancelLiveRun]);

  const run = activeLiveRun || liveRunDetail?.run;
  const cashflows = liveRunDetail?.cashflows || [];
  const dailyMarks = liveRunDetail?.dailyMarks || [];
  const notifications = liveRunDetail?.notifications || [];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'cashflows', label: 'Cash Flows' },
    { key: 'marks', label: 'Daily Marks' },
    { key: 'notifications', label: 'Alerts' },
  ];

  return (
    <div className="space-y-4">
      {/* Live badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          LIVE SIMULATION
        </span>
        {run && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLORS[run.status] || 'bg-gray-100'}`}>
            {STATUS_LABELS[run.status] || run.status}
          </span>
        )}
      </div>

      {/* No active run — show create button */}
      {!run && !loading && (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500 mb-4">
            Start a live simulation that tracks real market data as it arrives.
          </p>
          <button
            onClick={handleCreateRun}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            Start Live Simulation
          </button>
        </div>
      )}

      {loading && !run && (
        <div className="flex items-center justify-center py-12 gap-2 text-sm text-gray-400">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Active run details */}
      {run && (
        <>
          {/* Countdown */}
          <CountdownToEvent
            nextEventDate={run.next_event_date}
            nextEventType={run.next_event_type}
          />

          {/* Timeline */}
          <LiveTimeline
            cashflows={cashflows}
            currentStatus={run.status}
            nextEventType={run.next_event_type}
          />

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex gap-4">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab.label}
                  {tab.key === 'notifications' && notifications.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px]">
                      {notifications.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="min-h-[300px]">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Run summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Investment</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">
                      ${Number(run.investment_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tenor</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {run.tenor_days}-day
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Yield</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">
                      {run.gross_yield_local ? `${parseFloat(run.gross_yield_local).toFixed(2)}%` : 'Pending'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Forward</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {run.forward_mode === 'none' ? 'Unhedged' : run.forward_mode}
                    </p>
                  </div>
                </div>

                {/* Daily marks chart */}
                <ProjectionOverlay
                  dailyMarks={dailyMarks}
                  run={run}
                  sourceCurrency={run.source_currency}
                />
              </div>
            )}

            {activeTab === 'cashflows' && (
              <CashflowLedger cashflows={cashflows} runId={run.id} />
            )}

            {activeTab === 'marks' && (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-2 text-gray-500 font-medium">Date</th>
                      <th className="text-right py-2 px-2 text-gray-500 font-medium">Spot</th>
                      <th className="text-right py-2 px-2 text-gray-500 font-medium">Local Value</th>
                      <th className="text-right py-2 px-2 text-gray-500 font-medium">Unhedged</th>
                      <th className="text-right py-2 px-2 text-gray-500 font-medium">Hedged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyMarks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-400">
                          No daily marks recorded yet
                        </td>
                      </tr>
                    ) : (
                      dailyMarks.map((mark, idx) => (
                        <tr key={idx} className="border-b border-gray-50">
                          <td className="py-1.5 px-2 font-mono">{mark.mark_date}</td>
                          <td className="py-1.5 px-2 text-right font-mono">
                            {mark.spot_rate ? parseFloat(mark.spot_rate).toFixed(2) : '-'}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">
                            {mark.value_local ? Number(mark.value_local).toLocaleString() : '-'}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">
                            {mark.value_base_unhedged ? Number(mark.value_base_unhedged).toLocaleString() : '-'}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">
                            {mark.value_base_hedged ? Number(mark.value_base_hedged).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-2">
                {notifications.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">No notifications yet</p>
                ) : (
                  notifications.map((notif, idx) => (
                    <div key={idx} className="flex gap-3 p-3 rounded-lg bg-gray-50 text-xs">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{notif.subject}</p>
                        <p className="text-gray-500 mt-0.5">{notif.body}</p>
                      </div>
                      <span className="text-gray-400 flex-shrink-0">
                        {notif.created_at?.split('T')[0]}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => loadLiveRunDetail(run.id)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            {!['completed', 'cancelled'].includes(run.status) && (
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 ml-auto"
              >
                <XCircle className="w-3.5 h-3.5" />
                Cancel Run
              </button>
            )}
          </div>
        </>
      )}

      {/* Live run list */}
      {liveRuns.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Your Live Runs
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {liveRuns.map(lr => (
              <LiveRunCard
                key={lr.id}
                run={lr}
                isActive={run && run.id === lr.id}
                onClick={loadLiveRunDetail}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
